import { Close, Content, Description, Portal, Root, Title, Trigger } from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useScrollEdges } from '@/shared/hooks/useScrollEdges/useScrollEdges';
import { cn } from '@/shared/utils/cn/cn.util';

import { DialogOverlay } from '../Dialog/Dialog';
import { OVERLAY_CLOSE_CLASSES, OVERLAY_CLOSE_ROW_CLASSES } from '../Dialog/Dialog.tokens';
import { SCROLL_EDGE_CLASSES, SCROLLER_CLASSES } from '../scroll/scroll.tokens';

import { sheetVariants, type SheetVariants } from './Sheet.tokens';

/**
 * A side-docked dialog. Built on Radix's dialog primitive, so it inherits the
 * focus trap, the scroll lock and the Escape handling rather than reimplementing
 * three things that are easy to get subtly wrong.
 */
export const Sheet = Root;
export const SheetTrigger = Trigger;
export const SheetClose = Close;

export interface SheetContentProps
  extends ComponentPropsWithoutRef<typeof Content>, SheetVariants {}

export const SheetContent = forwardRef<ElementRef<typeof Content>, SheetContentProps>(
  function SheetContent({ className, children, side, ...props }, ref) {
    const { t } = useTranslation('uiKit');

    return (
      <Portal>
        <DialogOverlay />
        <Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
          {children}
          {/* Sticky, for the same reason as the dialog's: a sheet composed
              without a `SheetBody` scrolls as one piece, and an absolutely
              positioned close inside a scroller leaves with the content. */}
          <div className={OVERLAY_CLOSE_ROW_CLASSES}>
            <Close aria-label={t('modal.close')} className={OVERLAY_CLOSE_CLASSES}>
              <X aria-hidden className="h-4 w-4" />
            </Close>
          </div>
        </Content>
      </Portal>
    );
  },
);

export function SheetHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  // `shrink-0`, or a tall body squeezes the title away instead of scrolling.
  // `pe-2xl` keeps the sticky close row from landing on top of a long title.
  return (
    <div className={cn('flex shrink-0 flex-col gap-xs pe-2xl text-start', className)} {...props} />
  );
}

/**
 * The ONE scrolling region of a sheet — the sibling of `DialogBody`, and for the
 * same reasons, down to the same class set (`scroll.tokens.ts` explains what each
 * one is holding up; `min-h-0` is the load-bearing one).
 *
 * Anything long — a filter set, a nav list, a record's detail — belongs in here.
 * The footer does not: an action row inside the scroller is an action row the
 * operator has to go looking for.
 *
 * `data-sheet-body` is what tells the panel it may clip; the two `data-scroll-*`
 * attributes drive the edge rules, so a separator appears under the header only
 * while there is something above it.
 */
export function SheetBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  const { ref, isAtStart, isAtEnd } = useScrollEdges();

  return (
    <div
      ref={ref}
      data-sheet-body=""
      data-scroll-start={isAtStart}
      data-scroll-end={isAtEnd}
      className={cn(
        'flex flex-col gap-lg',
        // Full-bleed, so the edge rules reach the panel's borders and the
        // scrollbar rides its edge. The padding is given back inside.
        '-mx-xl px-xl',
        SCROLLER_CLASSES,
        SCROLL_EDGE_CLASSES,
        className,
      )}
      {...props}
    />
  );
}

export function SheetFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      // `mt-auto` still pushes it down when the sheet is taller than its content;
      // `shrink-0` pins it when the content is taller than the sheet.
      className={cn(
        'mt-auto flex shrink-0 flex-col-reverse gap-sm sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

export type SheetTitleProps = ComponentPropsWithoutRef<typeof Title>;
export type SheetDescriptionProps = ComponentPropsWithoutRef<typeof Description>;

export const SheetTitle = forwardRef<ElementRef<typeof Title>, SheetTitleProps>(function SheetTitle(
  { className, ...props },
  ref,
) {
  return <Title ref={ref} className={cn('text-h3 text-foreground', className)} {...props} />;
});

export const SheetDescription = forwardRef<ElementRef<typeof Description>, SheetDescriptionProps>(
  function SheetDescription({ className, ...props }, ref) {
    return (
      <Description
        ref={ref}
        className={cn('text-caption text-muted-foreground', className)}
        {...props}
      />
    );
  },
);
